import { z } from 'zod';

import i18n from '~/utils/i18n';

import { occurenceSchema } from './common';
import { dataMappingSchema } from './dataMapping';
import { ftpConnectionSchema } from './ftpConnection';
import { jsonApiConnectionSchema } from './jsonApiConnection';

/**
 * Discriminated union for connection types
 */
export const connectionSchema = z.discriminatedUnion('connectorType', [
  ftpConnectionSchema,
  jsonApiConnectionSchema,
]);

/**
 * Schema for creating a new data sync task
 * Combines data mapping and connection schemas
 */
export const createDataSyncTaskSchema = z.intersection(
  dataMappingSchema,
  connectionSchema,
);

/**
 * Full data sync task schema (for API responses)
 * Extends create schema with ID and status
 */
export const dataSyncTaskSchema = z.intersection(
  createDataSyncTaskSchema,
  z.object({
    id: z.uuid(),
    isActive: z.boolean(),
  }),
);

/**
 * Schema for creating a new data sync job
 */
export const createDataSyncJobSchema = z.object({
  name: z.string().min(1),
  occurence: occurenceSchema,
  // Which 3rd-party system the job syncs from. Set for you when a job is created
  // from a template, and pickable here so a hand-built job can be labelled too.
  sourceSystem: z.string().min(
    1,
    i18n.t(($) => $.template.validation.systemRequired, { ns: 'dataSync' }),
  ),
});

/**
 * Schema for updating an existing data sync job
 */
export const updateDataSyncJobSchema = createDataSyncJobSchema.extend({
  name: z.string().min(1).optional(),
});

/**
 * Full data sync job schema (for API responses)
 */
export const dataSyncJobSchema = z.object({
  companyAccountId: z.uuid(),
  createdById: z.union([z.uuid(), z.null()]),
  createdOn: z.coerce.date(),
  dataSyncTasks: z.array(dataSyncTaskSchema),
  id: z.uuid(),
  lastExecution: z.union([z.coerce.date(), z.null()]),
  modifiedById: z.union([z.uuid(), z.null()]),
  modifiedOn: z.coerce.date(),
  name: z.string().min(1),
  occurence: occurenceSchema,
  sourceSystem: z.string().min(1).nullable().optional(),
  templateParams: z.record(z.string(), z.unknown()).nullable().optional(),
  templateVersion: z.number().int().nullable().optional(),
});
